import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOperatorOptions } from '@/lib/api/accounts-queries';
import { useAdPlatformList } from '@/lib/api/ad-platforms-queries';
import { useAdProviderList } from '@/lib/api/ad-providers-queries';
import type { CreateAccountReq } from '@/lib/api/types';

interface CreateAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (req: CreateAccountReq) => void;
  isPending: boolean;
  error: Error | null;
}

export function CreateAccountDialog({
  open,
  onClose,
  onSubmit,
  isPending,
  error,
}: CreateAccountDialogProps) {
  const [platformId, setPlatformId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [accountName, setAccountName] = useState('');
  const [assignedOperatorId, setAssignedOperatorId] = useState('');
  const [remark, setRemark] = useState('');
  const [initialRecharge, setInitialRecharge] = useState('');
  const { data: operators = [] } = useOperatorOptions();
  const { data: platforms } = useAdPlatformList({ pageNo: 1, pageSize: 100, status: 1 });
  const { data: providers } = useAdProviderList({ pageNo: 1, pageSize: 100, status: 1 });

  const reset = () => {
    setPlatformId('');
    setProviderId('');
    setAccountCode('');
    setAccountName('');
    setAssignedOperatorId('');
    setRemark('');
    setInitialRecharge('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformId || !providerId || !accountCode.trim() || !accountName.trim()) return;
    onSubmit({
      platformId: Number(platformId),
      providerId: Number(providerId),
      accountCode: accountCode.trim(),
      accountName: accountName.trim(),
      assignedOperatorId: assignedOperatorId ? Number(assignedOperatorId) : undefined,
      remark: remark.trim() || undefined,
      // Send raw string to avoid float-precision loss; backend BigDecimal accepts numeric strings.
      initialRecharge: initialRecharge.trim(),
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const initialRechargeNum = parseFloat(initialRecharge);
  const canSubmit =
    Boolean(platformId) &&
    Boolean(providerId) &&
    accountCode.trim().length > 0 &&
    accountName.trim().length > 0 &&
    initialRecharge.trim().length > 0 &&
    Number.isFinite(initialRechargeNum) &&
    initialRechargeNum >= 0 &&
    !isPending;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && handleClose()}>
      <SheetContent className="w-[600px] overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>新建广告账户</SheetTitle>
          <SheetDescription>按广告平台、广告户商和账户信息创建广告账户。</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 pt-2 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ca-platform">广告平台</Label>
            <Select value={platformId} onValueChange={setPlatformId}>
              <SelectTrigger id="ca-platform">
                <SelectValue placeholder="选择 Meta / Google / TikTok" />
              </SelectTrigger>
              <SelectContent>
                {(platforms?.records ?? []).map((p) => (
                  <SelectItem key={p.platformId} value={String(p.platformId)}>
                    {p.platformName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ca-provider">广告户商</Label>
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger id="ca-provider">
                <SelectValue placeholder="选择广告账户提供商" />
              </SelectTrigger>
              <SelectContent>
                {(providers?.records ?? []).map((p) => (
                  <SelectItem key={p.providerId} value={String(p.providerId)}>
                    {p.providerName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ca-accountCode">广告账户 ID</Label>
            <Input
              id="ca-accountCode"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              placeholder="输入平台侧广告账户 ID"
              maxLength={128}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ca-accountName">账户名称</Label>
            <Input
              id="ca-accountName"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="输入账户名称"
              maxLength={64}
              required
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="ca-operator">分配投手（可选）</Label>
            <Select value={assignedOperatorId} onValueChange={setAssignedOperatorId}>
              <SelectTrigger id="ca-operator">
                <SelectValue placeholder="选择账户负责人" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op.userId} value={String(op.userId)}>
                    {op.displayName ? `${op.displayName} (${op.username})` : op.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="ca-remark">备注</Label>
            <Input
              id="ca-remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="输入备注信息"
              maxLength={200}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="ca-initialRecharge">首充金额</Label>
            <div className="flex gap-2">
              <Input
                id="ca-initialRecharge"
                type="number"
                step="0.01"
                min="0"
                max="99999999.99"
                value={initialRecharge}
                onChange={(e) => setInitialRecharge(e.target.value)}
                placeholder="0.00"
                required
              />
              <span className="flex h-10 items-center text-sm text-muted-foreground">USD</span>
            </div>
          </div>

          {error && <p className="text-sm text-destructive sm:col-span-2">{error.message}</p>}
          <SheetFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              取消
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? '创建中…' : '创建广告账户'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
